import { useCallback, useState } from "react";
import { navigate } from "gatsby";
import { clamp } from "lodash";
import styled from "styled-components";

import { Box, Link, Text } from "@vinomofo/components";
import { usePlanContext } from "../../hooks/wine-clubs/use-plan-context";
import { Button } from "../button";
import { ClubSummary } from "./ClubSummary";
import { FrequencySelector, PreferenceSelector, VariantSelector } from ".";
import { trackWineClubEvent } from "../../utils/event-tracking";
import useAuth from "../../hooks/use-auth";
import { ArrowLeftIcon } from "@vinomofo/icons";
import FinalSelector from "./FinalSelector";
import { COLORS } from "../../styles";

type PlanSelectorStep = {
  component: JSX.Element;
  next: { label: string; enabled: boolean };
  prev: { label: string; enabled: boolean };
};

const BackLink = styled(Link)`
  color: ${({ color }) => color};
  text-decoration: none;
  width: 100%;
  display: flex;
  align-items: center;
  align-content: center;
  svg {
    padding-top: 2px;
    padding-right: 2px;
    transition: 0.1s ease-in;
  }
  &:hover {
    cursor: pointer;
    text-decoration: underline;
    svg {
      transform: translateX(-25%);
    }
  }
`;

const StyledButtonWrapper = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
  max-width: 540px;
  margin: 0 auto;
  @media ${({ theme }) => theme.mq.max.mobile} {
    flex-direction: ${({ isLastStep }) => (isLastStep ? "column" : "row")};
    justify-content: ${({ isLastStep }) =>
      isLastStep ? "center" : "space-around"};
  }
`;

const StyledButtonContainer = styled(Box)`
  width: ${({ isLastStep }) => (isLastStep ? "100%" : "45%")};
  max-width: 250px;
  @media ${({ theme }) => theme.mq.max.mobile} {
    margin-bottom: ${({ isLastStep }) => (isLastStep ? "20px" : "0px")};
  }
`;

const getSteps = () => {
  const {
    variant,
    preference,
    frequency,
    variantOptions,
    preferenceOptions,
    frequencyOptions,
  } = usePlanContext();

  const steps: PlanSelectorStep[] = [];

  if (variantOptions.length > 1) {
    steps.push({
      component: <VariantSelector />,
      next: { label: "Next", enabled: undefined !== variant },
      prev: { label: "Prev", enabled: steps.length > 1 },
    });
  }

  if (preferenceOptions.length > 1) {
    steps.push({
      component: <PreferenceSelector />,
      next: { label: "Next", enabled: undefined !== preference },
      prev: { label: "Prev", enabled: steps.length >= 1 },
    });
  }

  if (frequencyOptions.length > 1) {
    steps.push({
      component: <FrequencySelector />,
      next: { label: "Next", enabled: undefined !== frequency },
      prev: { label: "Prev", enabled: steps.length >= 1 },
    });
  }

  steps.push({
    component: <FinalSelector />,
    next: { label: "Next", enabled: true },
    prev: { label: "Prev", enabled: steps.length >= 1 },
  });

  return steps;
};

export const ClubOptionPicker = (): JSX.Element => {
  const { club, variant, preference, frequency, planName } = usePlanContext();
  const [step, _setStep] = useState(1);
  const { user, isLoading, login, join, isAuthenticated } = useAuth();

  const steps = getSteps();
  const totalSteps = steps.length;

  const setStep = (index: number) => {
    _setStep(clamp(index, 1, steps.length));
  };

  const navigateToCheckout = () => {
    trackWineClubEvent(
      "Wine Club Selected",
      club,
      frequency,
      user ? user.sub : null,
      null,
      variant
    );
    isAuthenticated
      ? navigate(`/wine-clubs/checkout/${planName}`)
      : setStep(step + 1);
  };

  const handleLoginClick = useCallback(() => {
    const returnUrl = `/wine-clubs/checkout/${planName}`;
    login({
      returnTo: returnUrl,
    });
  }, [login, planName]);

  const handleCreateAccountClick = useCallback(() => {
    const returnUrl = `/wine-clubs/checkout/${planName}`;
    join({ returnTo: returnUrl });
  }, [join, planName]);

  const currentStep = steps[step - 1];
  const isLastStep = step === totalSteps;
  const isSecondLastStep = step === totalSteps - 1;

  if (undefined === currentStep) return null;

  return (
    <Box
      borderRadius="10px"
      overflow="hidden"
      backgroundColor="darkMofo"
      color="white"
      display="flex"
      flexDirection={["column", "column", "row"]}
    >
      <Box flexGrow="1" padding={4}>
        {isLastStep && (
          <BackLink
            onClick={() => setStep(step - 1)}
            display="flex"
            alignItems="center"
            color={club.color}
          >
            <ArrowLeftIcon color={COLORS[club.color]} />
            <Text
              variant="sm"
              color={club.color}
              fontWeight={"500"}
              fontSize={"15px"}
            >
              Back
            </Text>
          </BackLink>
        )}
        <Box mb={4}>{currentStep.component}</Box>
        <StyledButtonWrapper isLastStep={isLastStep}>
          {isLastStep ? (
            <>
              <StyledButtonContainer isLastStep={isLastStep}>
                <Button
                  backgroundColor={club.color}
                  color="white"
                  width="100%"
                  onClick={handleCreateAccountClick}
                >
                  Create an Account
                </Button>
              </StyledButtonContainer>
              <StyledButtonContainer isLastStep={isLastStep}>
                <Button
                  backgroundColor={club.color}
                  color="white"
                  width="100%"
                  onClick={handleLoginClick}
                >
                  Login
                </Button>
              </StyledButtonContainer>
            </>
          ) : (
            <>
              <StyledButtonContainer>
                <Button
                  backgroundColor={club.color}
                  color="white"
                  width="100%"
                  disabled={!currentStep.prev.enabled}
                  onClick={() => setStep(step - 1)}
                >
                  {currentStep.prev.label}
                </Button>
              </StyledButtonContainer>
              <StyledButtonContainer>
                <Button
                  backgroundColor={club.color}
                  color="white"
                  width="100%"
                  disabled={!currentStep.next.enabled || isLoading}
                  onClick={() =>
                    isSecondLastStep ? navigateToCheckout() : setStep(step + 1)
                  }
                >
                  {currentStep.next.label}
                </Button>
              </StyledButtonContainer>
            </>
          )}
        </StyledButtonWrapper>
      </Box>
      <Box
        backgroundColor="black"
        flexBasis="300px"
        flexShrink="0"
        flexGrow="0"
      >
        <Box padding="30px 40px">
          <ClubSummary
            club={club}
            variant={variant}
            preference={preference}
            frequency={frequency}
            dynamicVariant
            dynamicFrequency
          />
        </Box>
      </Box>
    </Box>
  );
};
